import React, { useState, useEffect } from 'react'
import { graphql, navigate } from 'gatsby'
import styled from 'styled-components'
import SEO from 'components/SEO'
import Layout from 'components/shared/Layout'
import HeroWithButtons from 'components/shared/HeroWithButtons'
import TextBlockWithLabel from '../components/shared/TextBlockWithLabel'
import TextWithTitle from '../components/shared/TextWithTitle'
import TextWithImage from '../components/shared/TextWithImage'
import TextWithUsps from '../components/shared/TextWithUsps'
import DownloadBrochure from '../components/shared/DownloadBrochure'
import Fases from '../components/shared/Fases'
import Webinars from '../components/shared/Webinars'
import Faq from '../components/shared/Faq'
import Cta from '../components/shared/Cta'
import GravityForm from 'components/GravityForm'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 100;
  background-color: rgba(0, 0, 0, 0.5);
`

const ModalBox = styled.div`
  max-height: 70vh;
  overflow: auto;
  max-width: 600px;
  background-color: ${({ theme }) => theme.color.face.light};
  border-radius: 15px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.45);
`

const ModalContent = styled.div`
  position: relative;
  color: ${({ theme }) => theme.color.text.main};
  padding: 1.5rem;

  & h2 {
    font-size: ${({ theme }) => theme.font.size.l};
  }

  & h3 {
    font-size: ${({ theme }) => theme.font.size.xsm};
  }
`

const ExitButton = styled.button`
  position: absolute;
  right: 15px;
  top: 15px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  & svg path,
  & svg line {
    stroke: ${({ theme }) => theme.color.text.main};
  }

  &:hover {
    opacity: 0.5;
  }
`

const ModalFrame = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.face.main};
  width: 100%;
  margin-top: 1rem;
  overflow: hidden;
`

export const pageQuery = graphql`
  query($wordpress_id: Int) {
    page: wordpressPage(wordpress_id: { eq: $wordpress_id }) {
      ...Nis2Frag
    }
  }
`

const getHash = () =>
  typeof window !== 'undefined' ? window.location.hash : ''

const PageTemplate = ({ data: { page } = {}, location }) => {
  const [hash, setHash] = useState(() => location?.hash || getHash() || '')

  useEffect(() => {
    setHash(location?.hash || getHash() || '')
  }, [location?.hash])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onHashChange = () => setHash(getHash() || '')
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const closeModal = () => {
    if (typeof window !== 'undefined' && location?.pathname) {
      navigate(location.pathname, { replace: true })
    }
  }

  if (!page) {
    return (
      <Layout>
        <div className="container py-5">
          <p>
            Deze pagina kon niet worden geladen. Controleer of de NIS2-pagina in
            WordPress de juiste template en ACF-velden heeft.
          </p>
        </div>
      </Layout>
    )
  }
  

  const { path, acf = {}, yoast_meta } = page
  const banner = acf?.banner
  const textBlockWithLabel = acf?.text_block
  const textWithTitle = acf?.text
  const textWithImage = acf?.text_image
  const textWithUsps = acf?.text_with_usp
  const downloadBrochure = acf?.download_brochure
  const fases = acf?.fases
  const textImageLeft = acf?.text_image_left
  const webinars = acf?.webinars
  const faq = acf?.faq
  const cta = acf?.cta

  return (
    <Layout>
      <SEO
        yoast={yoast_meta}
        path={path}
        image={{
          url: banner?.image?.localFile?.childImageSharp?.openGraph?.src,
        }}
      />

      <HeroWithButtons content={banner} />
      <TextBlockWithLabel content={textBlockWithLabel} />
      <TextWithTitle content={textWithTitle} />
      <TextWithImage content={textWithImage} />
      <TextWithUsps content={textWithUsps} />
      <DownloadBrochure content={downloadBrochure} />
      <Fases content={fases} />
      <TextWithImage
        content={textImageLeft}
        textPosition="right"
        id="text-with-image-left"
        className="mb-lg-3"
      />
      {webinars?.items?.length > 0 && (
        <Webinars content={webinars} />
      )}
      <Faq content={faq} />
      <Cta content={cta} id="afspraak" />

      {hash === '#afspraak-maken' && (
        <ModalOverlay
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Afspraak maken"
        >
          <div
            className="container h-100 d-flex align-items-center justify-content-center"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalBox>
              <ModalContent className="p-4 p-lg-5">
                <ExitButton
                  type="button"
                  onClick={closeModal}
                  aria-label="Sluiten"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </ExitButton>
                <h2>
                  <strong>Afspraak maken</strong>
                </h2>
                <ModalFrame>
                  <p style={{ padding: '1rem', color: '#555' }}>
                    Deze module volgt later.
                  </p>
                </ModalFrame>
              </ModalContent>
            </ModalBox>
          </div>
        </ModalOverlay>
      )}

      {hash === '#download' && (
        <ModalOverlay
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Download"
        >
          <div
            className="container h-100 d-flex align-items-center justify-content-center"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalBox>
              <ModalContent className="p-4 p-lg-5">
                <ExitButton
                  type="button"
                  onClick={closeModal}
                  aria-label="Sluiten"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </ExitButton>
                <h2>
                  <strong>Download</strong>
                </h2>
                <ModalFrame className="pt-5">
                  <GravityForm id={19} split={false} />
                </ModalFrame>
              </ModalContent>
            </ModalBox>
          </div>
        </ModalOverlay>
      )}

      <div className="my-3 my-lg-5" />
    </Layout>
  )
}

export default PageTemplate
